import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ChevronDown, Download } from 'lucide-react';
import {
  EXPORT_FORMATS,
  PAGINATION,
  WORD_STATUSES,
  type ExportFormat,
  type WordRecordView,
  type WordbookStats,
  type WordStatus,
} from '@en3/shared';
import { WORDBOOK_RELATED_KEYS, queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { useAuthStatus } from '@/api/hooks/useAuth';
import { wordbookApi } from '@/api/wordbook.api';
import { ROUTES } from '@/app/routes';
import { EmptyBlock, ErrorBlock, LoadingBlock, Pagination } from '@/components/common/States';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DEFAULT_WORDBOOK_FILTERS,
  WORD_STATUS_LABELS,
  WordbookFilters,
  type WordbookFilterValue,
} from '@/components/wordbook/WordbookFilters';
import { WordNoteDialog } from '@/components/wordbook/WordNoteDialog';
import { WordRecordRow } from '@/components/wordbook/WordRecordRow';

/**
 * 生词本页。
 *
 * 结构：登录态门 → 内容组件。把内容组件拆出来（而不是在同一个组件里 if 来 if 去）的理由是
 * **hook 的顺序与登录态无关**：未登录时不会建立任何生词本查询，也就不会出现"先请求一次 401
 * 再跳登录"的多余往返。
 *
 * 三件事值得说明：
 * 1. **筛选状态在这里**：一份值同时用于请求参数与 `queryKeys.wordbook(...)`，
 *    键里带上参数才能让"换筛选条件"自然命中不同的缓存条目（回到上一个条件时秒回，不用重新请求）。
 * 2. **每次筛选变化都把页码重置为 1**：否则会出现"在第 5 页换了状态，结果只剩 1 页"的空列表，
 *    用户会以为筛选没生效。
 * 3. **写操作后按 `WORDBOOK_RELATED_KEYS` 失效**：生词数量与今日待复习会显示在首页/个人中心，
 *    只失效 `['wordbook']` 会让别处显示旧数字。
 *
 * **标题约束（E2E `app-shell.spec.ts`）**：`/wordbook` 的 `<h1>` 文本必须**精确等于**「生词本」
 * （断言用的是 `toHaveText('生词本')`，多半一字就会失败），且在
 * 加载中 / 未登录 / 空 / 出错 / 有数据 时都要在。因此：
 * - h1 只放标题本身，统计（总数、今日待复习…）一律放 h1 下方；
 * - h1 在所有数据分支之外渲染，未登录态由 `AuthRequiredNotice` 渲染同一个 h1。
 */

/** 导出格式的说明文案：把"要不要加表头/什么分隔符"这类问题在点击前就说清楚。 */
const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: 'CSV（Excel 可直接打开）',
  anki: 'Anki 文本（制表符分隔）',
  json: 'JSON（含全部字段）',
};

/**
 * 未登录引导。
 *
 * 生词本页与复习页共用同一段文案与入口，只有功能名不同，所以由这里导出、复习页 import ——
 * 两页各写一份的话，文案迟早会漂移成"两个功能要求不一样"，而它们其实是同一条登录约束。
 */
export function AuthRequiredNotice({ feature, description }: { feature: string; description: string }) {
  return (
    <section className="mx-auto max-w-2xl px-5 py-10">
      <h1 className="text-2xl font-bold">{feature}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          to={ROUTES.login}
          className="inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm text-primary-foreground hover:opacity-90"
        >
          去登录
        </Link>
        <Link
          to={ROUTES.register}
          className="inline-flex h-10 items-center rounded-md border px-4 text-sm hover:bg-accent hover:text-accent-foreground"
        >
          注册账号
        </Link>
      </div>
    </section>
  );
}

export function WordbookPage() {
  const status = useAuthStatus();

  // 恢复登录态期间不要直接渲染"未登录"：那会让已登录用户每次刷新都看到一次登录引导
  if (status === 'idle' || status === 'loading') {
    return (
      <section className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-2xl font-bold">生词本</h1>
        <p role="status" className="mt-2 text-sm text-muted-foreground">
          正在恢复登录状态…
        </p>
      </section>
    );
  }

  if (status !== 'authenticated') {
    return <AuthRequiredNotice feature="生词本" description="生词本需要登录后才会保存：查过的词、笔记与复习进度都记在你的账号下。" />;
  }

  return <WordbookContent />;
}

/** 统计条上的一格。 */
function StatChip({ label, value, tone = 'default' }: { label: string; value: number; tone?: 'default' | 'primary' }) {
  return (
    <Badge tone={tone} className="px-3 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </Badge>
  );
}

function StatsBar({ stats }: { stats: WordbookStats }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <StatChip label="总数" value={stats.total} />
      {/* 待复习做成入口：看到数字的下一步一定是"那我去复习"，不该让用户自己去底部导航找 */}
      <Link to={ROUTES.review} className="rounded-full" aria-label={`今日待复习 ${stats.dueToday} 个，去复习`}>
        <Badge tone="primary" className="px-3 py-1 text-xs" title="去复习页">
          <span className="text-muted-foreground">今日待复习</span>
          <span className="font-semibold tabular-nums">{stats.dueToday}</span>
          <ArrowRight aria-hidden className="size-3" />
        </Badge>
      </Link>
      {WORD_STATUSES.map((status) => (
        <StatChip key={status} label={WORD_STATUS_LABELS[status]} value={stats.byStatus[status] ?? 0} />
      ))}
    </div>
  );
}

/**
 * 导出下拉。
 *
 * 刻意用"菜单 + 三个按钮"而不是三个并排按钮：移动端宽度有限，
 * 导出是低频操作，占三个按钮位会把高频的筛选挤下去。
 *
 * 导出走的是**文件流**（不是 JSON 信封），所以不能放进 useQuery 做缓存 ——
 * 用 mutation 表达"一次性副作用"更贴切。进行中禁用并显示 loading，
 * 避免用户连点三次导出三个文件。
 */
function ExportMenu({ status, dueOnly }: { status: WordStatus | 'all'; dueOnly: boolean }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const exportMutation = useMutation({
    mutationFn: (format: ExportFormat) =>
      wordbookApi.download(format, {
        status: status === 'all' ? undefined : status,
        due: dueOnly ? true : undefined,
      }),
  });

  useEffect(() => {
    if (!open) return;
    // 点外部 / Esc 关闭：用 mousedown 而不是 click，这样"按下就拖走"不会留下一层空菜单
    function onMouseDown(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const pending = exportMutation.isPending;

  function start(format: ExportFormat) {
    setOpen(false);
    setDone(false);
    exportMutation.reset();
    exportMutation.mutate(format, { onSuccess: () => setDone(true) });
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div ref={wrapperRef} className="relative">
        <Button
          variant="outline"
          loading={pending}
          onClick={() => {
            setOpen((value) => !value);
            setDone(false);
            exportMutation.reset();
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="导出当前筛选下的生词"
        >
          <Download aria-hidden className="size-4" />
          导出
          <ChevronDown aria-hidden className="size-3.5" />
        </Button>

        {open && (
          <div
            role="menu"
            aria-label="选择导出格式"
            className="absolute right-0 z-20 mt-1 w-60 rounded-md border bg-card p-1 text-card-foreground shadow-lg"
          >
            {EXPORT_FORMATS.map((format) => (
              <button
                key={format}
                type="button"
                role="menuitem"
                onClick={() => start(format)}
                className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              >
                {EXPORT_FORMAT_LABELS[format]}
              </button>
            ))}
            <p className="border-t px-3 py-2 text-[11px] text-muted-foreground">
              导出会带上当前的状态与“只看今天到期”筛选。
            </p>
          </div>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">Anki 导入时选择制表符分隔。</p>
      {pending && (
        <p role="status" className="text-[11px] text-muted-foreground">
          正在生成文件…
        </p>
      )}
      {done && !pending && (
        <p role="status" className="text-[11px] text-muted-foreground">
          已开始下载。
        </p>
      )}
      {exportMutation.isError && (
        <p role="alert" className="text-[11px] text-destructive">
          导出失败：{toDisplayMessage(exportMutation.error)}
        </p>
      )}
    </div>
  );
}

function WordbookContent() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<WordbookFilterValue>(DEFAULT_WORDBOOK_FILTERS);
  const [page, setPage] = useState(1);
  /** 清空筛选时用它给筛选栏换 key（见下方 <WordbookFilters key>） */
  const [resetToken, setResetToken] = useState(0);
  const [editing, setEditing] = useState<WordRecordView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const params = {
    page,
    pageSize: PAGINATION.defaultPageSize,
    status: filters.status === 'all' ? undefined : filters.status,
    due: filters.dueOnly ? true : undefined,
    q: filters.q.length > 0 ? filters.q : undefined,
    tag: filters.tag.length > 0 ? filters.tag : undefined,
    sort: filters.sort,
  };

  const list = useQuery({
    queryKey: queryKeys.wordbook(params),
    queryFn: () => wordbookApi.list(params),
  });

  /** 筛选变化一律回第 1 页：停在旧页码会看到"筛选后没有结果"的假空态。 */
  const handleFilterChange = useCallback((patch: Partial<WordbookFilterValue>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_WORDBOOK_FILTERS);
    setPage(1);
    // 换 key 让筛选栏重挂载：只重置 value 的话，输入框里"正在防抖等待"的那次搜索
    // 会在 300ms 后把刚清掉的条件又写回来（输入框的本地文本不在 filters 里）。
    setResetToken((token) => token + 1);
  }, []);

  const remove = useMutation({
    // 把"这条是不是本页最后一条"一起传进来：删除回调里就能直接决定要不要回退页码，
    // 不需要读渲染期的列表快照（在渲染期间写 ref 会被 react-hooks/purity 判为不纯）。
    mutationFn: (target: { id: string; lastOnPage: boolean }) => wordbookApi.remove(target.id),
    onSuccess: async (_data, target) => {
      // 删掉的是本页最后一条：回退一页，否则用户会停在一个空页上，
      // 看到"没有内容"却不知道还能往回翻（原先靠 effect 兜住的场景，
      // 现在在事件发生的时刻处理，没有额外渲染，也不与渲染顺序较劲）。
      if (target.lastOnPage && page > 1) setPage(page - 1);
      await Promise.all(WORDBOOK_RELATED_KEYS.map((key) => queryClient.invalidateQueries({ queryKey: key })));
    },
  });

  const items = list.data?.items ?? [];

  /**
   * 标签候选：接口没有"标签字典"端点，所以从当前页数据里就地收集。
   * 代价是只覆盖当前可见页 —— 因此把**正在使用的标签**也并入候选，
   * 否则翻到第二页后下拉里的已选项会消失，看起来像筛选器坏了。
   */
  const tags = useMemo(() => {
    const collected = new Set<string>();
    for (const item of list.data?.items ?? []) {
      for (const tag of item.tags) collected.add(tag);
    }
    if (filters.tag.length > 0) collected.add(filters.tag);
    return Array.from(collected).sort((a, b) => a.localeCompare(b, 'zh-CN'));
  }, [list.data, filters.tag]);

  const hasActiveFilters =
    filters.q.length > 0 ||
    filters.status !== 'all' ||
    filters.dueOnly ||
    filters.tag.length > 0 ||
    filters.sort !== DEFAULT_WORDBOOK_FILTERS.sort;

  return (
    <section className="mx-auto max-w-4xl px-5 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {/* h1 只放标题本身（E2E 精确匹配「生词本」）；统计在下方单独一排，不进标题 */}
          <h1 className="text-2xl font-bold">生词本</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            阅读时查过或标记过的词都会到这里，按记忆曲线安排复习。
          </p>
        </div>
        <ExportMenu status={filters.status} dueOnly={filters.dueOnly} />
      </div>

      <div className="mt-5">
        {list.data ? (
          <StatsBar stats={list.data.stats} />
        ) : (
          <p role="status" className="text-xs text-muted-foreground">
            {list.isError ? '统计暂时不可用。' : '正在加载统计…'}
          </p>
        )}
      </div>

      <div className="mt-5">
        <WordbookFilters key={resetToken} value={filters} onChange={handleFilterChange} tags={tags} />
      </div>

      {notice && (
        <p role="status" className="mt-4 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          {notice}
        </p>
      )}

      {remove.isError && (
        <p role="alert" className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          删除失败：{toDisplayMessage(remove.error)}
        </p>
      )}

      <div className="mt-4">
        {list.isPending ? (
          <LoadingBlock label="正在加载生词…" />
        ) : list.isError ? (
          <ErrorBlock message={toDisplayMessage(list.error)} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          hasActiveFilters ? (
            <EmptyBlock
              title="筛选后没有结果"
              description="换个条件试试，或者清空筛选看全部生词。"
              action={
                <Button variant="outline" onClick={resetFilters}>
                  清空筛选
                </Button>
              }
            />
          ) : (
            <EmptyBlock
              title="还没有生词"
              description="在阅读页点词查义后标记「模糊 / 不认识」，词就会自动进到这里。"
              action={
                <Link
                  to={ROUTES.textbooks}
                  className="inline-flex h-9 items-center rounded-md border px-3 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  去阅读文章
                </Link>
              }
            />
          )
        ) : (
          <>
            <ul className="space-y-3">
              {items.map((record) => (
                <WordRecordRow
                  key={record.id}
                  record={record}
                  onEdit={setEditing}
                  onDelete={(target) => remove.mutate({ id: target.id, lastOnPage: items.length <= 1 })}
                  deleting={remove.isPending && remove.variables?.id === record.id}
                />
              ))}
            </ul>
            <Pagination page={page} totalPages={list.data?.totalPages ?? 0} onChange={setPage} />
          </>
        )}
      </div>

      {editing !== null && (
        <WordNoteDialog
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice('笔记已保存。');
          }}
        />
      )}
    </section>
  );
}
